import UserReducer from '../../reducers/UserReducer'
import { getDataOrDefault, HttpMethods, makeQueryString, makeQueryStringFromObject } from '../apiHelper'
import { ExportTypes, ImportModes, LanguageTags } from '../constants'
import { downloadFile, getIdOrNull, requestMultipart, requestWithPromise } from '../index'
import apiClient from './apiClient'

const DEFAULT_PARAM_ADD_USER = { userToAdd: -1 }
const DEFAULT_PARAM_ATTACH_TO_SECTORS = { ...DEFAULT_PARAM_ADD_USER, sectors: [-1] }

export const SectorContexts = Object.freeze({
  ACCEPTED_SECTIONS: 'acceptedSections'
})

export const getAllSectors = (user, paramaters = {}) => {
  return requestWithPromise(`/api/sectors${makeQueryString(paramaters)}`, HttpMethods.GET, null, user)
}

export const getSectorDocuments = (user, sector) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/documents`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getPlaces = (sector, user, parameters = {}) => {
  return requestWithPromise(
    `/quota/sectors/${getIdOrNull(sector)}${makeQueryStringFromObject(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const fetchSectorScore = (user, sector, endDate, startDate, additionalData) => {
  return requestWithPromise(
    `/sector/${getIdOrNull(sector)}/evaluation-internships/score?end-date=${endDate}&start-date=${startDate}&additional-data=${additionalData}`,
    HttpMethods.GET,
    null,
    user
  ).then(getDataOrDefault({}))
}

export const getSectorPresets = (user, sector) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/shift-presets`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getInternshipsBySector = (user, sector) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/internships`,
    HttpMethods.GET,
    null,
    user
  )
}

export const createSector = (user, sector) => {
  return requestWithPromise(
    '/api/sectors',
    HttpMethods.POST,
    sector,
    user
  )
}

/** @deprecated 2024-06-06 */
export const createSectorOld = (user, sector) => {
  return requestWithPromise(
    '/api/Sector',
    HttpMethods.POST,
    sector,
    user
  )
}

export const updateSector = (user, sector) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}`,
    HttpMethods.PATCH,
    sector,
    user
  )
}

/** @deprecated 2024-06-06 */
export const updateSectorOld = (user, sector, body) => {
  return requestWithPromise(
    `/api/Sector/${getIdOrNull(sector)}`,
    HttpMethods.PATCH,
    body,
    user
  )
}

export const archiveSector = (user, sector, archive = true) => {
  return requestWithPromise(
    `/sector/archive/${getIdOrNull(sector)}`,
    HttpMethods.PATCH,
    { toArchive: archive },
    user
  )
}

export const deleteSector = (user, sector) => {
  return requestWithPromise(
    `/api/sector/${getIdOrNull(sector)}`,
    HttpMethods.DELETE,
    null,
    UserReducer
  )
}

export const switchSectorInstitution = (user, sector, institution) => {
  return requestWithPromise(
    `/sectors/${getIdOrNull(sector)}/transfer`,
    HttpMethods.PATCH,
    { targetInstitution: institution.id },
    user
  )
}

export const assignManagedBy = (user, sector, idsManagedBy) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/users`,
    HttpMethods.PATCH,
    { users: idsManagedBy },
    user
  )
}

export const addSectorStudentPathways = (user, sector, pathways = []) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/student-pathways`,
    HttpMethods.POST,
    pathways,
    user
  )
}

export const getSectorStudentPathways = (user, sector, parameters = {}) => (
  apiClient.get(user, `api/sectors/${getIdOrNull(sector)}/student-pathways`, parameters)
)

export const removeSectorStudentPathways = (user, sector, pathways) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/student-pathways${makeQueryString({ ids: pathways })}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const addSectorActTypes = (user, sector, actTypes = []) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/act-types`,
    HttpMethods.POST,
    { ids: actTypes },
    user
  )
}

export const getSectorActTypes = (user, sector) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/act-types`,
    HttpMethods.GET,
    null,
    user
  )
}

export const removeSectorActTypes = (user, sector, actTypes) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}/act-types${makeQueryString({ ids: actTypes })}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const exportSectors = (user, parameters = { language: LanguageTags.EN, type: ExportTypes.CSV }) => {
  return requestWithPromise(
    '/export/sectors',
    HttpMethods.POST,
    parameters,
    user
  )
}

export const getSector = (user, sector) => {
  return requestWithPromise(
    `/api/sectors/${getIdOrNull(sector)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getSectorSections = (user, sector) => apiClient.get(user, `api/sectors/${getIdOrNull(sector)}/sections`)

export const getSectorQuotas = (user, sector, parameters = {}) => apiClient.get(user, `api/sectors/${getIdOrNull(sector)}/quotas`, parameters)

export const deleteSectorPreset = (user, sector, preset) => apiClient.delete(user, `api/sectors/${getIdOrNull(sector)}/shift-presets/${getIdOrNull(preset)}`)

export const addContactPerson = (user, sector, body = DEFAULT_PARAM_ADD_USER) => {
  return requestWithPromise(`/api/sectors/${getIdOrNull(sector)}/contact-persons`, HttpMethods.POST, body, user)
}

export const addManagedUser = (user, sector, body = DEFAULT_PARAM_ADD_USER) => {
  return requestWithPromise(`/api/sectors/${getIdOrNull(sector)}/managed-user`, HttpMethods.POST, body, user)
}

export const addContactPersonToMultipleSectors = (user, body = DEFAULT_PARAM_ATTACH_TO_SECTORS) => {
  return requestWithPromise('/sectors/contact-persons', HttpMethods.POST, body, user)
}

export const addManagedUserToMultipleSectors = (user, body = DEFAULT_PARAM_ATTACH_TO_SECTORS) => {
  return requestWithPromise('/sectors/managed-users', HttpMethods.POST, body, user)
}

export const getSectorSectionGroups = (user, sector) => apiClient.get(user, `api/sectors/${getIdOrNull(sector)}/section-groups`)

export const getSectorsImportTemplate = user => {
  return downloadFile(
    '/import/sectors/template',
    'sectors_import_template.xlsx',
    user,
    HttpMethods.GET,
    null,
    { rejectOnErrorBody: true }
  )
}

export const importSectors = (user, file, mode = ImportModes.DRY_RUN) => {
  return requestMultipart('/import/sectors', HttpMethods.POST, { file, mode }, user).then(response => {
    if (!response || response.status === 'error' || !Array.isArray(response.data)) {
      return Promise.reject(response || new Error('UNEXPECTED'))
    }

    return response
  })
}
